/**
 * Every client-side Arabic string the chat shell renders, in one place.
 *
 * MIRRORS `chat-agent/src/channels/copy.ts`: named exports, SCREAMING_SNAKE constants, zero
 * imports, and one module per concern (`toolLabels.ts` for the D-06 status lines, `suggestions.ts`
 * for the D-15 chips, this file for everything else). The reason for the shape is the same reason
 * the server side has it: a copy lint needs ONE import surface. `__tests__/chatCopy.test.ts`
 * iterates the exports of these three modules and asserts no string carries an em dash or an en
 * dash, which is the client half of the rule `ARABIC_COPY_DASH_PATTERN` already enforces on the
 * server (`copy.ts:162`).
 *
 * EVERY STRING BELOW IS TRANSCRIBED FROM `38-UI-SPEC.md` § "Copywriting Contract" AND IS BINDING.
 * The wording there is settled: measured, reviewed and signed off. Do not "improve" a line here
 * without changing the contract first, and in particular do not add an apology, a promise of a
 * later answer, or a regenerate invitation to any of them.
 *
 * ZERO IMPORTS, on purpose. The no-dash suite runs under vitest's `environment: 'node'` with no `@`
 * alias configured (see `frontend/vitest.config.ts`), so a copy module that reached for React, for
 * `ChatContext` or for the alias would take the only automated check of this rule down with it.
 */

// ─── The escape hatch's URL ──────────────────────────────────────────────────
//
// Moved here from the floating WhatsApp button component, which phase 38 deleted. It is the
// canonical support number and mirrors `SOCIALS.whatsapp` in `Footer.tsx`. The header's
// `كمّل على واتساب` link was removed in 39-08 (product decision: web runs v2 and answers for
// itself), so the constant currently has no chat-shell consumer; it stays here because a URL that
// two files each hold their own copy of is how a support number goes stale in one place and not
// the other.
export const WHATSAPP_URL = 'https://wa.me/201555987590';

// ─── Launcher ────────────────────────────────────────────────────────────────
//
// The unread signal is carried HERE, in the accessible name, and not in an `aria-live` region: D-04
// is a page-load check, so announcing it on every navigation would be noise. The dot itself is
// `aria-hidden`.
export const LAUNCHER_OPEN_LABEL = 'افتح شات ناصح';
export const LAUNCHER_OPEN_UNREAD_LABEL = 'افتح شات ناصح، فيه رسالة جديدة';

// ─── Empty state (D-02) ──────────────────────────────────────────────────────
export const EMPTY_STATE_HEADING = 'أهلاً، أنا ناصح.';
export const EMPTY_STATE_BODY = 'اسألني عن أي متجر مصري وأنا أقولك رأي الناس فيه.';

// ─── Header ──────────────────────────────────────────────────────────────────
//
// `PANEL_SUBLINE` is an honest disclosure that this is a bot, in the same spirit as the deleted
// WhatsApp button's docblock reasoning: tapping the corner must not read as "start a chat with a
// human".
export const PANEL_TITLE = 'ناصح';
export const PANEL_SUBLINE = 'بوت يتفرج';
export const WHATSAPP_LINK_LABEL = 'كمّل على واتساب';
export const CLOSE_LABEL = 'اقفل الشات';

// ─── Message list: the author prefix ─────────────────────────────────────────
//
// VISUALLY HIDDEN, and it is the third author cue rather than a nicety. Colour plus alignment plus
// this prefix are the three: alignment alone fails for a screen reader, which hears no geometry, and
// colour alone fails at low vision. Rendered inside each turn's list item, before the bubble, so the
// reading order is "who, then what".
//
// The bot prefix is spelled out rather than composed from `PANEL_TITLE`: the header's title and a
// spoken speaker label are two different strings that happen to share a word today, and joining them
// would make a future header rename silently rewrite what a screen reader says.
export const AUTHOR_PREFIX_BOT = 'ناصح:';
export const AUTHOR_PREFIX_USER = 'أنت:';

// ─── Composer ────────────────────────────────────────────────────────────────
//
// Both action labels carry their object. A bare `ابعت` is a verb with no object, which a screen
// reader announces as "send, button" with nothing being sent.
export const COMPOSER_PLACEHOLDER_IDLE = 'اسأل ناصح عن أي متجر...';
export const COMPOSER_PLACEHOLDER_STREAMING = 'ناصح بيرد دلوقتي...';
export const COMPOSER_PLACEHOLDER_RATE_LIMITED = 'استنى شوية وابعت تاني...';
export const SEND_LABEL = 'ابعت الرسالة';
export const STOP_LABEL = 'وقف الرد';

// ─── Rate limit wall (D-16, D-18) ────────────────────────────────────────────
//
// Rendered as a SYSTEM row, never as a bot bubble: this is not ناصح talking, and dressing a refusal
// as the bot's voice is the dishonesty the tone target rejects.
//
// `اريحنا شوية` is lifted verbatim from the live `RATE_LIMIT_MESSAGE` (`messengerService.ts:13`) so
// the web channel sounds like the same bot. The 😊 is deliberately NOT carried over: that emoji
// exists to warm up an unstyled plain-text DM, and a styled panel row does not need it.
export const LIMIT_COUNTDOWN_PLACEHOLDER = '{MM:SS}';
export const LIMIT_ROW_ANONYMOUS = 'بعتت رسايل كتير في وقت قصير. سجل دخول عشان تكمل دلوقتي، أو استنى {MM:SS}.';
export const LIMIT_ROW_AUTHENTICATED = 'بعتت رسايل كتير في وقت قصير. اريحنا شوية وارجع بعد {MM:SS}.';
export const LOGIN_NUDGE_LABEL = 'سجل دخول';

// ─── Store carousel cards (D-06, WEB-03) ─────────────────────────────────────
//
// The only visible string a card renders that is not store data. It follows the review COUNT, as in
// `4.6 ★ 40 تقييم`, and it is the singular form on purpose: Arabic takes the singular after a
// numeral above ten, and the card's rating row is only ever shown at three reviews or more (D-06),
// so this reads correctly across the whole range the card can display.
//
// The logo image needs no copy constant of its own: the store name sits right beside it, so the
// image is decorative and carries `alt=""` with `aria-hidden="true"`. A repeated "شعار المتجر" on
// every card would make a screen reader read the word "logo" ten times before the first store name.
export const STORE_CARD_REVIEWS_SUFFIX = 'تقييم';

// The contact icon row (39-08). Icon-only controls, so each label is the WHOLE accessible name of
// its link: a screen reader must hear the action, not "link, link, link".
export const STORE_CARD_CALL_LABEL = 'اتصل بالمتجر';
export const STORE_CARD_WHATSAPP_LABEL = 'راسل المتجر على واتساب';
export const STORE_CARD_MAPS_LABEL = 'شوف المكان على خرائط جوجل';

// ─── Errors and connection ───────────────────────────────────────────────────
export const ERROR_STREAM_FAILED = 'مش قادر أوصل للسيرفر دلوقتي. جرب تاني بعد شوية.';
export const ERROR_SEND_FAILED = 'الرسالة مبعتتش. جرب تاني.';
export const ERROR_STOP_FAILED = 'مقدرتش أوقف الرد. استنى لما يخلص.';
export const ERROR_ENGINE_NOT_STREAMING = 'الشات على الموقع مش متاح دلوقتي. كلمني على واتساب وأنا موجود.';
export const ERROR_RATE_LIMITED = 'بعتت رسايل كتير في وقت قصير. استنى شوية وابعت تاني.';
export const ERROR_INVALID_REQUEST = 'مقدرتش أبعت الرسالة دي. جرب تكتبها تاني.';
export const ERROR_STREAM_UNSUPPORTED = 'المتصفح ده مش بيدعم الشات. كلمني على واتساب وأنا موجود.';
export const RETRY_LABEL = 'جرب تاني';

/**
 * Spend-degrade disclosure (D-17). Appears ONLY when a deep tool was actually refused mid run, as a
 * muted footer inside the bot's own bubble. No panel banner: D-17 rejected the always-on banner,
 * and this line does not promise a later answer (the ceiling may hold for hours) or blame the user.
 */
export const DEGRADE_NOTE = 'شغال دلوقتي بالمصادر السريعة بس، فمقدرتش أعمل بحث موسع للسؤال ده.';

/**
 * The error copy, keyed by the CLIENT's error codes (`chatClientErrorMap`, `chatStreamClient.ts`).
 *
 * Keyed off the code set rather than by a chain of `if`s at the render site, mirroring
 * `webAdapter.ts`'s `blockKindMap` habit, so adding a code is a decision made in exactly one place.
 * `ChatContext` already narrows what can reach a rendered row: it collapses every server error
 * string to `internal_error` precisely so this map has a closed key set and can never render an
 * untranslated machine string to a visitor. The three session codes and `aborted` are recovered
 * from or ignored upstream and are present only so the record is total.
 */
export const CHAT_ERROR_COPY = {
  session_required: ERROR_STREAM_FAILED,
  session_unknown: ERROR_STREAM_FAILED,
  session_expired: ERROR_STREAM_FAILED,
  rate_limited: ERROR_RATE_LIMITED,
  engine_not_streaming: ERROR_ENGINE_NOT_STREAMING,
  invalid_request: ERROR_INVALID_REQUEST,
  internal_error: ERROR_STREAM_FAILED,
  network_error: ERROR_STREAM_FAILED,
  reconnect_exhausted: ERROR_STREAM_FAILED,
  stream_unsupported: ERROR_STREAM_UNSUPPORTED,
  server_only: ERROR_STREAM_FAILED,
  aborted: ERROR_STREAM_FAILED,
} as const;

export type ChatErrorCopyKey = keyof typeof CHAT_ERROR_COPY;

/** Total by construction: an unknown code falls back to the honest "cannot reach the server" line. */
export const errorCopyForCode = (code: string | null | undefined): string => {
  if (typeof code === 'string' && code in CHAT_ERROR_COPY) {
    return CHAT_ERROR_COPY[code as ChatErrorCopyKey];
  }
  return ERROR_STREAM_FAILED;
};

/**
 * `MM:SS` for the limit row's countdown.
 *
 * Deliberately NOT localised into Arabic-Indic digits: the rest of the site renders counts and
 * ratings in Western digits, and a countdown is the one place a reader is scanning for a number.
 */
export const formatCountdown = (totalSeconds: number): string => {
  const safe = Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.ceil(totalSeconds) : 0;
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/**
 * The limit row's full sentence, with the countdown substituted into the contract's own placeholder.
 *
 * The substitution happens here rather than at the render site so the only Arabic prose in play is
 * still the exported constant the no-dash suite checks.
 */
export const limitRowCopy = ({
  authenticated,
  countdownSec,
}: {
  authenticated: boolean;
  countdownSec: number;
}): string => {
  const template = authenticated ? LIMIT_ROW_AUTHENTICATED : LIMIT_ROW_ANONYMOUS;
  return template.replace(LIMIT_COUNTDOWN_PLACEHOLDER, formatCountdown(countdownSec));
};
